import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Tooltip,
    Box,
    CircularProgress
} from '@mui/material';
import {
    Group as GroupIcon,
    PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useDefaultPermissions } from '../hooks/useDefaultPermissions';
import { useUserNotifications } from '../hooks/useUserNotifications';
import { useTeamManagement } from './hooks/useTeamManagement';
import { TeamList } from './TeamList';
import { AddUsersDialog } from './AddUsersDialog';
import { EditUserDialog } from './EditUserDialog';
import { useTheme } from '../../../../components/hooks/useTheme';
import { useNotification } from '../../../../components/index';

export const TeamManagementPanel = ({
    id,
    inpitIdentifier,
    currentUserAccess,
    fileRecords,
    onCreatorTransferred = null
}) => {
    const { theme } = useTheme();
    const { sendTeamNotifications } = useUserNotifications();
    const [teamDialogOpen, setTeamDialogOpen] = useState(false);
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
    const [selectedUserTaskAccess, setSelectedUserTaskAccesses] = useState(null);
    const {
        showSuccess,
        showError
    } = useNotification();

    const {
        team,
        availableUsers,
        loading,
        loadingAvailableUsers,
        loadTeam,
        refreshData,
        handleAddMultipleUserTaskAccesses,
        handleUpdateTaskAccesses,
        handleDeleteTaskAccess,
        handleTransferCreatorRole
    } = useTeamManagement(inpitIdentifier, currentUserAccess, showSuccess, showError);

    const { getDefaultPermissionsForRole } = useDefaultPermissions();
    const canManageTeam = currentUserAccess?.canManageTeam === true;

    // Получаем только оригинальные файлы
    const originalFiles = useMemo(() => {
        if (!fileRecords || !Array.isArray(fileRecords)) return [];

        const filteredFiles = fileRecords.filter(record => {
            const version = record.version !== undefined ? record.version :
                record.version !== undefined ? record.version : 1;
            return version === 0 || version === 1;
        });

        return filteredFiles.map((record, index) => ({
            baseFileId: record.baseFileId,
            fileName: record.fileName || record.originalFileName || `file_${index}`,
            fileVersion: record.version !== undefined ? record.version :
                record.version !== undefined ? record.version : 1,
            filePath: record.filePath || '',
            isOriginal: true
        }));
    }, [fileRecords]);

    const handleOpenTeamDialog = async () => {
        setTeamDialogOpen(true);
        await refreshData();
    };

    const canModifyUser = (targetUserTaskAccess) => {
        if (!currentUserAccess?.canManageTeam) return false;
        if (targetUserTaskAccess.isCurrentUser) return false;
        if (targetUserTaskAccess.role === 'Creator') return false;
        return true;
    };

    const handleEditUser = (event, userTaskAccess) => {
        if (!userTaskAccess || !canModifyUser(userTaskAccess)) {
            return;
        }

        setSelectedUserTaskAccesses(userTaskAccess);
        setEditUserDialogOpen(true);
    };

    const handleTeamDialogClose = () => {
        setSelectedUserTaskAccesses(null);
        setTeamDialogOpen(false);
    }

    const handleSendTeamNotifications = async (userLogins, actionType) => {
        await sendTeamNotifications(userLogins, actionType, currentUserAccess?.user, id);
    };

    const teamTooltipContent = () => {
        if (canManageTeam) {
            return <Box sx={{ p: 0.5 }}>Настройка команды</Box>;
        }
        return (<Box sx={{ p: 0.5 }}>Просмотр команды</Box>);
    };

    return (
        <>
            <Tooltip title={teamTooltipContent()}>
                <IconButton
                    size="small"
                    onClick={handleOpenTeamDialog}
                    className="share-button"
                >
                    <GroupIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Диалог управления командой */}
            <Dialog
                open={teamDialogOpen}
                onClose={handleTeamDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {canManageTeam ? "Управление командой" : "Список участников команды"}
                    {canManageTeam &&
                        <Button
                            startIcon={<PersonAddIcon />}
                            onClick={() => setAddUserDialogOpen(true)}
                            style={{ float: 'right' }}
                            className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                        >
                            Добавить пользователей
                        </Button>
                    }
                </DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TeamList
                            team={team}
                            currentUserAccess={currentUserAccess}
                            onEditUserAssess={handleEditUser}
                            handleDeleteUserAccess={handleDeleteTaskAccess}
                            onUserTaskAccessDeleted={refreshData}
                            onTransferCreatorRole={handleTransferCreatorRole}
                            onCreatorTransferred={onCreatorTransferred}
                            onSendNotifications={handleSendTeamNotifications}
                            theme={theme}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setTeamDialogOpen(false)}
                        className="dt1520-button active-dt1520-button view-button default-button">
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог добавления пользователей */}
            <AddUsersDialog
                open={addUserDialogOpen}
                onClose={() => setAddUserDialogOpen(false)}
                availableUsers={availableUsers}
                loadingAvailableUsers={loadingAvailableUsers}
                originalFiles={originalFiles}
                getDefaultPermissionsForRole={getDefaultPermissionsForRole}
                onUserTaskAccessAdded={refreshData}
                handleAddMultipleUserTaskAccesses={handleAddMultipleUserTaskAccesses}
                currentUserAccess={currentUserAccess}
                onSendNotifications={handleSendTeamNotifications}
                theme={theme}
            />

            {/* Диалог редактирования пользователя */}
            <EditUserDialog
                open={editUserDialogOpen}
                onClose={() => {
                    setEditUserDialogOpen(false);
                    setSelectedUserTaskAccesses(null);
                }}
                selectedUserTaskAccess={selectedUserTaskAccess}
                originalFiles={originalFiles}
                getDefaultPermissionsForRole={getDefaultPermissionsForRole}
                onUserTaskAccessUpdated={() => {
                    loadTeam();
                    showSuccess('Права пользователя успешно обновлены');
                }}
                handleUpdateTaskAccesses={handleUpdateTaskAccesses}
                currentUserAccess={currentUserAccess}
                onSendNotifications={handleSendTeamNotifications}
                theme={theme}
            />
        </>
    );
};